import React, { useEffect, useState, useRef, useMemo } from "react";
import { Sliders, ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { Button } from "react-bootstrap";
import OrdersTable from "./OrdersTable/OrdersTable";
import HeaderControls from "./HeaderControls/HeaderControls";
import AddToSummaryButton from "./AddToSummaryButton";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import { io } from "socket.io-client";
import {
    formatRangeDateForApi,
    formatRangeDayMonthInAppTz,
    getDayMetric,
    normalizeProductName,
} from "../helpers/utils";
import {
    computeMonthTurnoverBarModel,
    MONTH_TURNOVER_SOURCE_SEGMENTS,
} from "../helpers/computeMonthTurnoverBarModel.js";
import { caseFoldForProductMatch } from "../lib/productNameNormalize/engine.js";
import { getProductLinePid } from "../helpers/getProductLinePid";
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { ALLOWEDSTATUSIDS, BOT_TOKEN, CHAT_ID_AT, TIMEZONE } from "../constans";
import 'dayjs/locale/uk';
import DeleteFromSummaryButton from "./DeleteFromSummaryButton";
import MonthlyTurnoverProgressBar from "./MonthlyTurnoverProgressBar.jsx";
import MonthTurnoverSourceSharePie from "./MonthTurnoverSourceSharePie.jsx";
import DirectManagerDialogConversion from "./DirectManagerDialogConversion.jsx";
import {
    computeDirectManagerDialogConversion,
    computeOfflineManagerStats,
    pickReportManagersFromSchedule,
    buildDirectShiftUserIdsByPeriod,
    summaryDirectNames,
} from "../helpers/directManagerDialogConversion.js";
import {
    ymListIntersectingKyivRange,
    buildScheduleShiftStatsByReactKey,
} from "../helpers/scheduleWorkingDaysLookup.js";
import useClickOutside from "../hooks/useClickOutside";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.locale("uk");

/**
 * Live «сьогодні» для PR: не тягнути, якщо користувач дивиться повний минулий календарний місяць
 * (наприклад квітень, коли вже травень — інакше з’являється зайвий рядок 1 травня).
 */
function shouldFetchPrLiveTodayRange(range) {
    const s = dayjs(range.startDate).tz(TIMEZONE);
    const e = dayjs(range.endDate).tz(TIMEZONE);
    if (!s.isValid() || !e.isValid()) return false;
    const isFullCalendarMonth =
        s.isSame(s.startOf("month"), "day") &&
        e.isSame(s.endOf("month"), "day") &&
        s.isSame(e, "month");
    if (isFullCalendarMonth && s.isBefore(dayjs().tz(TIMEZONE), "month")) {
        return false;
    }
    const startStr = s.format("YYYY-MM-DD");
    const endStr = e.format("YYYY-MM-DD");
    const todayStr = dayjs().tz(TIMEZONE).format("YYYY-MM-DD");
    return startStr <= todayStr && todayStr <= endStr;
}

/** Порожній / усі null у Mongo kyivWeather не повинні перекривати overlay із синку Open-Meteo (Number(null)==0!). */
function kyivWeatherDocHasForecastTemps(w) {
    if (!w || typeof w !== "object") return false;
    const check = (v) => v != null && Number.isFinite(Number(v));
    return check(w.tempMaxC) || check(w.tempMinC) || check(w.tempMeanC);
}

/** Логування PR-погоди: у консолі браузера `localStorage.setItem('PR_DEBUG_WEATHER','1')` або dev-режим npm. */
function shouldLogPrWeather() {
    try {
        if (typeof localStorage !== "undefined" && localStorage.getItem("PR_DEBUG_WEATHER") === "1") {
            return true;
        }
    } catch {
        /* ignore */
    }
    return process.env.NODE_ENV === "development";
}

const ReportLayout = ({
    title = "",
    preset,
    customContent,
    onSummariesLoaded,
    productSpendList,
    showProductTables,
    primaryAction,
    /** Опційний блок у рядку з кнопками «Налаштування» / primaryAction (наприклад, лічильник Instagram). */
    topToolbarExtra,
    user,
    /** Після `/api/live-summary-today` — оновити рядок «сьогодні» в Google PR (C:D, F:G, J:T; з live також AN+). */
    syncGoogleSheetsLiveToday = false,
    /** Назва листа в Google (наприклад «Квітень 2026»); має збігатися з бекендом. */
    googleSheetsSheetTitle,
    /** Опційно: показати в UI, чи викликано синк, пропуск чи помилку. */
    onGoogleSheetsTodaySyncResult,
    /** Live-рядок «сьогодні»: актуальні підписники з API (як у шапці PR+). */
    liveInstagramFollowersCount = null,
    liveInstagramFollowersLoading = false,
    /** З `setRange` — період з батька (PR+ / експорт Google з відповідним місяцем). */
    range: rangeProp,
    setRange: setRangeProp,
}) => {
    const controlled = typeof setRangeProp === "function" && rangeProp != null;
    const [uncontrolledRange, setUncontrolledRange] = useState(() => ({
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("month").toDate(),
    }));
    const range = controlled ? rangeProp : uncontrolledRange;
    const setRange = controlled ? setRangeProp : setUncontrolledRange;

    const [ordersByPeriod, setOrdersByPeriod] = useState([]);
    const [adsByPeriod, setAdsByPeriod] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [orders, setOrders] = useState([]);
    const [ads, setAds] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [currency, setCurrency] = useState("UAH");
    const [usdBuyRate, setUsdBuyRate] = useState(null);
    const [usdSellRate, setUsdSellRate] = useState(null);
    /** Погода з Open-Meteo (повертається з sync; для днів без Summary — лише тут). Зокрема «сьогодні» — добові мін/макс, без «нічної» поточної точки. */
    const [kyivWeatherOverlay, setKyivWeatherOverlay] = useState({});
    /** Рядок live «сьогодні»: окремий GET до Forecast API добових min/max (Historical bulk інколи без «сьогодні» в UI). */
    const [prLiveForecastDailyByDate, setPrLiveForecastDailyByDate] = useState({});
    const [rangeDate, setRangeDate] = useState("day");
    const [isChecked, setIsChecked] = useState(false);
    const [selectedSources, setSelectedSources] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedManagers, setSelectedManagers] = useState([]);
    const [tagFilterMode, setTagFilterMode] = useState("all");
    const [summaries, setSummaries] = useState([]);
    const [liveOrders, setLiveOrders] = useState([]);
    const [liveCampaigns, setLiveCampaigns] = useState([]);
    const [tempRange, setTempRange] = useState(() => ({
        startDate: (controlled && rangeProp ? rangeProp.startDate : null) || dayjs().startOf("month").toDate(),
        endDate: (controlled && rangeProp ? rangeProp.endDate : null) || dayjs().endOf("month").toDate(),
    }));
    const [adsFromDb, setAdsFromDb] = useState([]);
    const [showAdminButtons, setShowAdminButtons] = useState(false);
    /** Графік /schedule: shifts по YYYY-MM для підрахунку робочих днів менеджерів. */
    const [scheduleByYm, setScheduleByYm] = useState({});
    const [scheduleYmLoadStatus, setScheduleYmLoadStatus] = useState("idle");
    const filtersPanelRef = useRef(null);

    const rangeRef = useRef(range);
    useEffect(() => {
        rangeRef.current = range;
    }, [range]);

    const presetRef = useRef(preset);
    useEffect(() => {
        presetRef.current = preset;
    }, [preset]);

    const rangeDateRef = useRef(rangeDate);
    useEffect(() => {
        rangeDateRef.current = rangeDate;
    }, [rangeDate]);

    const sheetTitleRef = useRef(googleSheetsSheetTitle);
    useEffect(() => {
        sheetTitleRef.current = googleSheetsSheetTitle;
    }, [googleSheetsSheetTitle]);

    const onGoogleSheetsTodaySyncResultRef = useRef(onGoogleSheetsTodaySyncResult);
    useEffect(() => {
        onGoogleSheetsTodaySyncResultRef.current = onGoogleSheetsTodaySyncResult;
    }, [onGoogleSheetsTodaySyncResult]);

    useEffect(() => {
        setTempRange({
            startDate: range.startDate,
            endDate: range.endDate,
        });
    }, [range.startDate, range.endDate]);

    useClickOutside(filtersPanelRef, () => setShowFilters(false));

    const isAdmin = user?.role === "admin"

    const fetchLiveTodayData = async () => {
        const r = rangeRef.current;
        const rd = rangeDateRef.current;
        const startStr = formatRangeDateForApi(r.startDate);
        const endStr = formatRangeDateForApi(r.endDate);
        const todayStr = dayjs().tz(TIMEZONE).format("YYYY-MM-DD");
        const todayInFilter = startStr <= todayStr && todayStr <= endStr;

        if (
            !presetRef.current?.fetchLiveToday ||
            rd !== "day" ||
            !shouldFetchPrLiveTodayRange(r)
        ) {
            setLiveOrders([]);
            setLiveCampaigns([]);
            return;
        }

        try {
            console.log("[SheetsTodaySync client] fetchLiveTodayData: старт", {
                syncGoogleSheetsLiveToday: Boolean(syncGoogleSheetsLiveToday),
            });
            const res = await fetch("/api/live-summary-today");
            const data = await res.json();
            setLiveOrders(data.orders || []);
            setLiveCampaigns(data.campaigns || []);

            if (syncGoogleSheetsLiveToday) {
                const LOG = "[SheetsTodaySync client]";
                const titleNow = sheetTitleRef.current;
                console.log(LOG, "після live-summary-today", {
                    syncGoogleSheetsLiveToday,
                    startStr,
                    endStr,
                    todayStr,
                    monthBody: formatRangeDateForApi(r.startDate),
                    inRange: todayInFilter,
                });
                const notify = (payload) => {
                    const fn = onGoogleSheetsTodaySyncResultRef.current;
                    if (typeof fn === "function") fn(payload);
                };
                const body = {
                    month: formatRangeDateForApi(r.startDate),
                    liveCampaigns: Array.isArray(data.campaigns) ? data.campaigns : [],
                    liveOrders: Array.isArray(data.orders) ? data.orders : [],
                    ...(titleNow ? { sheetTitle: titleNow } : {}),
                };
                console.log(LOG, "POST /api/google-sheets/sync-test-report-today-row", {
                    month: body.month,
                    liveCampaigns: `[${body.liveCampaigns.length} items]`,
                    liveOrders: `[${body.liveOrders.length} items]`,
                });
                void fetch("/api/google-sheets/sync-test-report-today-row", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "same-origin",
                        body: JSON.stringify(body),
                    })
                        .then(async (httpRes) => {
                            const text = await httpRes.text();
                            let json;
                            try {
                                json = JSON.parse(text);
                            } catch {
                                json = null;
                            }
                            console.log(LOG, "відповідь HTTP", httpRes.status, { text: text.slice(0, 500), json });
                            if (!httpRes.ok) {
                                throw new Error(json?.message || `HTTP ${httpRes.status}: ${text.slice(0, 200)}`);
                            }
                            return json;
                        })
                        .then((payload) => {
                            const r0 = payload?.result;
                            if (r0?.skipped) {
                                console.warn(LOG, "сервер: skipped", r0);
                                notify({
                                    type: "skipped",
                                    message:
                                        `Google «${titleNow || "PR"}»: не оновлено — сьогодні не в обраному місяці фільтра.`,
                                    detail: r0,
                                });
                            } else {
                                console.log(LOG, "сервер: успіх", r0);
                                notify({
                                    type: "success",
                                    message: `Google «${titleNow || "PR"}»: записано рядок ${r0?.sheetRow ?? "?"} за ${r0?.today ?? todayStr} (C:D, F:G, J:T${r0?.ranges?.productMatrix ? ", AN+" : ""}).`,
                                    detail: r0,
                                });
                            }
                        })
                        .catch((e) => {
                            console.error(LOG, "помилка fetch/parse", e);
                            notify({
                                type: "error",
                                message: `Google PR: помилка синку — ${e?.message || e}. Перевірте SPREADSHEET_TT_2026_ID та назву листа.`,
                            });
                        });
            }
        } catch (e) {
            console.error("Помилка завантаження live-даних за сьогодні", e);
        }
    };

    const fetchLiveTodayDataRef = useRef(fetchLiveTodayData);
    fetchLiveTodayDataRef.current = fetchLiveTodayData;

    const applyPreset = (preset) => {
        setSelectedSources(preset.sources || []);
        setSelectedStatuses(preset.statuses || []);
        setSelectedManagers(preset.selectedManagers || []);
        setTagFilterMode(preset.tagFilterMode || "all");
        setRangeDate(preset.rangeDate || "day");
        setShowFilters(false);

        if (preset.fetchLiveToday) {
            fetchLiveTodayData();
        }
    };

    const currentPreset = preset;

    useEffect(() => {
        const fetchAds = async () => {
            try {
                const start = formatRangeDateForApi(range.startDate);
                const end = formatRangeDateForApi(range.endDate);

                const res = await fetch(`/api/fb/daily-range?from=${start}&to=${end}`);
                const data = await res.json();

                setAdsFromDb(data || []);

            } catch (e) {
                console.error("Помилка завантаження реклами:", e);
            }
        };

        fetchAds();
    }, [range]);
    console.log("adsFromDb", adsFromDb)

    useEffect(() => {
        if (!summaries || summaries.length === 0) {
            setOrdersByPeriod([]);
            setAdsByPeriod([]);
            return;
        }

        const filterCampaigns = (campaigns) => {
            if (!selectedSources.length) return campaigns;

            const isInstagramSelected =
                selectedSources.includes(1) || selectedSources.includes(4);

            const isSiteSelected = selectedSources.includes(9);

            return campaigns.filter(ad => {
                if (!ad.source) return true;
                const isInstagram = ["lava1", "lava2", "lava4"].includes(ad.source);
                const isSite = ad.source === "lava3";

                if (isSiteSelected && isSite) return true;
                if (isInstagramSelected && isInstagram) return true;

                return false;
            });
        };


        const groupedByDate = summaries.reduce((acc, summary) => {
            const date = summary.date;
            if (!acc[date]) {
                acc[date] = {
                    orders: [],
                    campaigns: [],
                };
            }

            // ✅ Добавляем заказы как есть
            acc[date].orders.push(...(summary.orders || []));

            // ✅ Сначала фильтруем кампании, затем добавляем
            const filteredCampaigns = filterCampaigns(summary.campaigns || []);
            acc[date].campaigns.push(...filteredCampaigns.map(c => ({ ...c, date })));

            return acc;
        }, {});

        const groupedOrders = Object.values(groupedByDate).map(group => group.orders);
        const groupedAds = Object.values(groupedByDate).map(group => group.campaigns);

        setOrdersByPeriod(groupedOrders);
        setAdsByPeriod(groupedAds);
    }, [summaries, selectedSources]); // 👈 добавляем selectedSources в зависимости


    useEffect(() => {
        const socket = io();
        const onWebhook = () => {
            void fetchLiveTodayDataRef.current();
        };
        socket.on("webhook-triggered", onWebhook);
        return () => socket.off("webhook-triggered", onWebhook);
    }, []);

    useEffect(() => {
        if (
            shouldFetchPrLiveTodayRange(range) &&
            rangeDate === "day" &&
            preset?.fetchLiveToday
        ) {
            fetchLiveTodayData();
        } else {
            setLiveOrders([]);
            setLiveCampaigns([]);
        }
    }, [range, rangeDate, preset?.fetchLiveToday]);

    useEffect(() => {
        const start = formatRangeDateForApi(range.startDate);
        const end = formatRangeDateForApi(range.endDate);
        const todayStr = dayjs().tz(TIMEZONE).format("YYYY-MM-DD");
        const isTodayOnly = start === end && start === todayStr;

        // За сьогодні в БД немає записів — тільки live з KeyCRM (preset.fetchLiveToday).
        if (isTodayOnly && preset?.fetchLiveToday) {
            setSummaries([]);
            if (onSummariesLoaded) onSummariesLoaded([]);
            if (preset?.syncKyivWeather && preset?.columns?.kyivWeather) {
                (async () => {
                    try {
                        const wr = await fetch("/api/summary/sync-kyiv-weather", {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ start, end }),
                        });
                        const wj = await wr.json().catch(() => ({}));
                        if (wr.ok && wj.weatherByDate && typeof wj.weatherByDate === "object") {
                            setKyivWeatherOverlay(wj.weatherByDate);
                            if (shouldLogPrWeather()) {
                                const keys = Object.keys(wj.weatherByDate);
                                console.debug("[PR weather] POST sync (today-only range)", {
                                    start,
                                    end,
                                    persisted: wj.persisted,
                                    dayKeys: keys.length,
                                    todayKeyIncluded: keys.includes(start),
                                    todayPayload: wj.weatherByDate[start],
                                });
                            }
                        } else if (shouldLogPrWeather()) {
                            console.debug("[PR weather] POST sync (today-only range) skipped/fail", {
                                httpOk: wr.ok,
                                bodySample: typeof wj === "object" ? Object.keys(wj) : null,
                                wj,
                            });
                        }
                    } catch (we) {
                        console.warn("Kyiv weather sync (today live)", we);
                    }
                })();
            } else {
                setKyivWeatherOverlay({});
            }
            return;
        }

        const fetchSummary = async () => {
            setLoading(true);
            try {
                const res = await fetch(`/api/summary?start=${start}&end=${end}`);
                const data = await res.json();
                let next = data;
                if (preset?.syncKyivWeather && preset?.columns?.kyivWeather) {
                    try {
                        const wr = await fetch("/api/summary/sync-kyiv-weather", {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ start, end }),
                        });
                        const wj = await wr.json().catch(() => ({}));
                        if (wr.ok && wj.weatherByDate && typeof wj.weatherByDate === "object") {
                            setKyivWeatherOverlay(wj.weatherByDate);
                            if (shouldLogPrWeather()) {
                                const keys = Object.keys(wj.weatherByDate);
                                console.debug("[PR weather] POST sync (full range)", {
                                    start,
                                    end,
                                    kyivToday: todayStr,
                                    persisted: wj.persisted,
                                    dayKeys: keys.length,
                                    todayKeyIncluded: keys.includes(todayStr),
                                    todayPayload: wj.weatherByDate[todayStr],
                                });
                            }
                        } else if (shouldLogPrWeather()) {
                            console.debug("[PR weather] POST sync (full range) skipped/fail", {
                                httpOk: wr.ok,
                                bodySample: typeof wj === "object" ? Object.keys(wj) : null,
                                wj,
                            });
                        }
                        const r2 = await fetch(`/api/summary?start=${start}&end=${end}`);
                        if (r2.ok) next = await r2.json();
                    } catch (we) {
                        console.warn("Kyiv weather sync", we);
                    }
                } else {
                    setKyivWeatherOverlay({});
                }
                setSummaries(next);
                if (onSummariesLoaded) onSummariesLoaded(next);
            } catch (error) {
                console.error("Помилка завантаження summary:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSummary();
    }, [range, preset?.fetchLiveToday, preset?.syncKyivWeather, preset?.columns?.kyivWeather]);

    useEffect(() => {
        const DEFAULT_RATE = 43;

        const fetchRates = async () => {
            try {
                const res = await fetch("/api/exchange-rates");

                if (!res.ok) {
                    throw new Error("Exchange rate request failed");
                }

                const data = await res.json();

                const usd = Array.isArray(data)
                    ? data.find(c => c.currencyCodeA === 840 && c.currencyCodeB === 980)
                    : null;

                if (usd?.rateBuy && usd?.rateSell) {

                    // ✅ сохраняем в localStorage
                    localStorage.setItem("usdBuyRate", usd.rateBuy);
                    localStorage.setItem("usdSellRate", usd.rateSell);

                    setUsdBuyRate(usd.rateBuy);
                    setUsdSellRate(usd.rateSell);

                    console.log("💰 Курс завантажено з API");

                } else {
                    throw new Error("USD rate not found");
                }

            } catch (err) {
                console.warn("⚠️ Помилка завантаження курсу, пробуємо localStorage");

                const savedBuy = localStorage.getItem("usdBuyRate");
                const savedSell = localStorage.getItem("usdSellRate");

                if (savedBuy && savedSell) {
                    setUsdBuyRate(Number(savedBuy));
                    setUsdSellRate(Number(savedSell));
                    console.log("💾 Курс завантажено з localStorage");
                } else {
                    setUsdBuyRate(DEFAULT_RATE);
                    setUsdSellRate(DEFAULT_RATE);
                    console.warn("⚠️ Використовується типовий курс");
                }
            }
        };

        fetchRates();
    }, []);



    useEffect(() => {
        if (preset) {
            applyPreset(preset);
        }
    }, [preset]);

    useEffect(() => {
        if (!summaries || summaries.length === 0) {
            setOrders([]);
            setAds([]);
            return;
        }
        const allOrders = summaries.flatMap(s => s.orders || []);
        const allAds = summaries.flatMap(s => (s.campaigns || []).map(c => ({ ...c, date: s.date })));
        setOrders(allOrders);
        setAds(allAds);
    }, [summaries]);

    const groupedByDate = summaries.reduce((acc, summary) => {
        const date = summary.date;
        if (!acc[date]) {
            acc[date] = {
                orders: [],
                campaigns: [],
                leads: 0, // 👈
            };
        }

        acc[date].orders.push(...(summary.orders || []));
        acc[date].campaigns.push(...(summary.campaigns || []));
        acc[date].leads = {
            instagram: Math.floor((summary.leads?.instagram ?? 0) * 0.9),
            telegram: summary.leads?.telegram ?? 0,
        };
        acc[date].tickets = summary.tickets ?? { instagram: 0, telegram: 0 };
        return acc;
    }, {});

    const leadsByPeriod = Object.values(groupedByDate).map(group => group.leads);
    const ticketsByPeriod = Object.values(groupedByDate).map(group => group.tickets || {});

    const getTotalPayments = (orders = []) => orders.reduce((sum, o) => sum + (o.payments_total || 0), 0);
    const getTotalUpsales = (orders = []) => orders.reduce((sum, o) => sum + (o.products?.filter(p => p.upsale).length || 0), 0);
    const getRepeatSalesWithTag5 = (orders = []) => orders.filter(o => o.tags?.some(t => t.id === 5)).length;
    const getSaleOrdersWithTag6 = (orders = []) => orders.filter(o => o.tags?.some(t => t.id === 6)).length;
    const getTotalPurchased = (orders = []) => orders.reduce((sum, o) => sum + (o.products?.reduce((acc, p) => acc + (p.purchased_price || 0), 0) || 0), 0);
    const getTotalMargin = (orders = []) => orders.reduce((sum, o) => sum + (o.products?.reduce((acc, p) => acc + (p.price_sold - p.purchased_price || 0), 0) || 0), 0);

    const filteredOrders = orders.filter(order => {
        const hasBarterTag = order.tags?.some(tag => tag.id === 1);
        const hasWholesaleTag = order.tags?.some(tag => tag.id === 4);

        const matchesTagFilter = () => {
            switch (tagFilterMode) {
                case 'no_opt':
                    return !hasWholesaleTag;
                case 'no_barter':
                    return !hasBarterTag;
                case 'no_both':
                    return !hasBarterTag && !hasWholesaleTag;
                case 'only_barter':
                    return hasBarterTag;
                case 'only_opt':
                    return hasWholesaleTag;
                default:
                    return true;
            }
        };

        const matchesSourceFilter = selectedSources.length === 0 || selectedSources.includes(order.source_id);
        const matchesManagerFilter = selectedManagers.length === 0 || selectedManagers.includes(order.manager?.id);

        const matchesStatusFilter = () => {
            if (ALLOWEDSTATUSIDS.length === 0) return true;
            return ALLOWEDSTATUSIDS.includes(order.status_id);
        };



        return (
            matchesTagFilter() &&
            matchesSourceFilter &&
            matchesManagerFilter &&
            matchesStatusFilter()
        );
    });

    const kyivTodayYmd = dayjs().tz(TIMEZONE).format("YYYY-MM-DD");

    const filteredLiveOrders = useMemo(() => {
        return liveOrders.filter((order) => {
            const hasBarterTag = order.tags?.some((tag) => tag.id === 1);
            const hasWholesaleTag = order.tags?.some((tag) => tag.id === 4);

            const matchesTagFilter = () => {
                switch (tagFilterMode) {
                    case "no_opt":
                        return !hasWholesaleTag;
                    case "no_barter":
                        return !hasBarterTag;
                    case "no_both":
                        return !hasBarterTag && !hasWholesaleTag;
                    case "only_barter":
                        return hasBarterTag;
                    case "only_opt":
                        return hasWholesaleTag;
                    default:
                        return true;
                }
            };

            const matchesSourceFilter =
                selectedSources.length === 0 || selectedSources.includes(order.source_id);
            const matchesManagerFilter =
                selectedManagers.length === 0 || selectedManagers.includes(order.manager?.id);

            const matchesStatusFilter = () => {
                if (ALLOWEDSTATUSIDS.length === 0) return true;
                return ALLOWEDSTATUSIDS.includes(order.status_id);
            };

            return (
                matchesTagFilter() &&
                matchesSourceFilter &&
                matchesManagerFilter &&
                matchesStatusFilter()
            );
        });
    }, [liveOrders, tagFilterMode, selectedSources, selectedManagers, selectedStatuses]);

    /**
     * PR live «сьогодні»: окремий добовий прогноз Open-Meteo (Forecast API), щоб завжди були мін/макс за добу
     * незалежно від того, чи historical bulk sync вклав ключ у kyivWeatherOverlay.
     */
    useEffect(() => {
        const needPresets =
            preset?.columns?.kyivWeather && preset?.syncKyivWeather && preset?.fetchLiveToday;
        if (!needPresets) {
            setPrLiveForecastDailyByDate({});
            return;
        }

        const startStr = formatRangeDateForApi(range.startDate);
        const endStr = formatRangeDateForApi(range.endDate);
        const todayStr = kyivTodayYmd;
        const todayInRange = startStr <= todayStr && todayStr <= endStr;
        if (!todayInRange || filteredLiveOrders.length === 0) {
            setPrLiveForecastDailyByDate({});
            return;
        }

        let cancelled = false;
        void (async () => {
            const q = shouldLogPrWeather() ? "?debug=1" : "";
            try {
                if (shouldLogPrWeather()) {
                    console.debug("[PR weather] GET kyiv-forecast-daily-today …", {
                        todayStr,
                        liveOrderCount: filteredLiveOrders.length,
                    });
                }
                const res = await fetch(`/api/weather/kyiv-forecast-daily-today${q}`, {
                    credentials: "include",
                });
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (!res.ok || !data?.ok || !data.weather || typeof data.weather !== "object") {
                    if (shouldLogPrWeather()) {
                        console.debug("[PR weather] GET kyiv-forecast-daily-today fail", {
                            http: res.status,
                            data,
                        });
                    }
                    return;
                }
                if (shouldLogPrWeather()) {
                    console.debug("[PR weather] GET kyiv-forecast-daily-today ok", {
                        matchedDailyRow: data.matchedDailyRow,
                        todayYmd: data.todayYmd,
                        weather: data.weather,
                        debug: data.debug,
                    });
                }
                const ymd = typeof data.todayYmd === "string" ? data.todayYmd : todayStr;
                setPrLiveForecastDailyByDate({ [ymd]: data.weather });
            } catch (e) {
                if (shouldLogPrWeather()) {
                    console.debug("[PR weather] GET kyiv-forecast-daily-today exception", e);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        preset?.columns?.kyivWeather,
        preset?.syncKyivWeather,
        preset?.fetchLiveToday,
        range.startDate,
        range.endDate,
        kyivTodayYmd,
        filteredLiveOrders.length,
    ]);

    /**
     * На PR за поточну добу є і Summary у Mongo (частково), і `/api/live-summary-today`.
     * Раніше live додавався другим періодом — ті самі замовлення суми двічі (або помилкові qty у колонках).
     */
    const ordersForReports = (() => {
        if (!filteredLiveOrders.length) return filteredOrders;
        const withoutKyivTodayFromDb = filteredOrders.filter((o) => {
            const d = dayjs(o.created_at).tz(TIMEZONE).format("YYYY-MM-DD");
            return d !== kyivTodayYmd;
        });
        return [...withoutKyivTodayFromDb, ...filteredLiveOrders];
    })();

    /** Місячний ТО (PR+): факт та прогноз; сума збігів з таблицею — ordersForReports (з live за сьогодні). По джерелах: IG [1,4], сайт [9], шоурум Київ [10]. */
    const monthTurnoverBarComputed = preset?.showMonthTurnoverBar
        ? computeMonthTurnoverBarModel(ordersForReports, range.startDate)
        : null;
    const monthTurnoverBarModel =
        typeof monthTurnoverBarComputed === "object" && monthTurnoverBarComputed !== null
            ? monthTurnoverBarComputed
            : null;
    /** Частки поточного факт-місячного ТО по джерелах збігаються з сегментами діаграми-кільця */
    const monthTurnoverChannelRows = useMemo(() => {
        if (monthTurnoverBarModel == null) return [];
        const rows = MONTH_TURNOVER_SOURCE_SEGMENTS.map((seg) => ({
            key: seg.key,
            label: seg.label,
            emoji: seg.emoji,
            model: computeMonthTurnoverBarModel(
                ordersForReports.filter((o) => seg.sourceIds.includes(Number(o.source_id))),
                range.startDate
            ),
        })).filter((row) => row.model != null);
        const totals = rows.map((r) => Math.max(0, Number(r.model.totalSoFar) || 0));
        const sum = totals.reduce((a, b) => a + b, 0);
        return rows.map((row, i) => ({
            ...row,
            factShareOfMonthPct: sum > 0 ? (totals[i] / sum) * 100 : 0,
        }));
    }, [monthTurnoverBarModel, ordersForReports, range.startDate]);

    const combinedData = ordersForReports.flatMap(order =>
        order.products.map(product => {
            const pid = getProductLinePid(product);

            return {
                pid,
                name: normalizeProductName(product.name),
                quantity: product.quantity || 0,
                purchased_price: product.purchased_price || 0,
                margin: (product.price_sold || 0) - (product.purchased_price || 0),
            };
        })
    ).reduce((acc, item) => {
        const existing = acc.find(el => el.pid === item.pid);
        if (existing) {
            existing.quantity += item.quantity;
            existing.purchased_price += item.purchased_price;
            existing.margin += item.margin;
        } else {
            acc.push({ ...item });
        }
        return acc;
    }, []);



    const summaryByProduct = combinedData.reduce((acc, item) => {
        const pid = item.pid;

        if (!acc[pid]) {
            acc[pid] = {
                pid,
                name: item.name,          // для отображения
                quantity: 0,
                purchased_price: 0,
                margin: 0
            };
        }

        acc[pid].quantity += item.quantity;
        acc[pid].purchased_price += item.purchased_price;
        acc[pid].margin += item.margin;

        return acc;
    }, {});

    const summaryByProductArrPerPid = Object.values(summaryByProduct);

    summaryByProductArrPerPid.sort((a, b) => b.quantity - a.quantity);

    /** Колонки таблиці: одна заголовкова назва навіть при різних product_id поза KeyCRM, якщо нормалізований текст збігається. */
    const productBreakdownColumns = (() => {
        const byFolded = new Map();
        for (const p of summaryByProductArrPerPid) {
            const label = String(p.name ?? "").trim();
            const k = label ? caseFoldForProductMatch(label) : `_pid:${p.pid ?? ""}`;
            if (!byFolded.has(k)) {
                byFolded.set(k, { pid: label ? null : p.pid != null ? Number(p.pid) : null, name: label || p.name || "" });
            }
        }
        return [...byFolded.values()].filter((col) => col.name);
    })();

    const normalize = normalizeProductName;

    const aggregatedSpend = (productSpendList || []).reduce((acc, item) => {
        const pid = item.pid;
        if (!pid) return acc;

        if (!acc[pid]) {
            acc[pid] = {
                pid,
                name: item.name,
                spend: 0
            };
        }

        acc[pid].spend += item.spend;
        return acc;
    }, {});



    const aggregatedSales = summaryByProductArrPerPid.reduce((acc, item) => {
        const pid = item.pid;
        if (!pid) return acc;

        if (!acc[pid]) {
            acc[pid] = {
                pid,
                name: normalize(item.name), // только для отображения
                quantity: 0,
                purchased_price: 0,
                margin: 0,
            };
        }

        acc[pid].quantity += item.quantity;
        acc[pid].purchased_price += item.purchased_price;
        acc[pid].margin += item.margin;

        return acc;
    }, {});


    const allPids = new Set([
        ...Object.keys(aggregatedSpend),
        ...Object.keys(aggregatedSales)
    ]);

    const result = Array.from(allPids).map(pid => {
        const spendUSD = aggregatedSpend[pid]?.spend || 0;
        const quantity = aggregatedSales[pid]?.quantity || 0;
        const marginUAH = aggregatedSales[pid]?.margin || 0;

        const name =
            aggregatedSales[pid]?.name ||
            aggregatedSpend[pid]?.name ||
            `PID ${pid}`;

        const usdRate = usdSellRate || 40;
        const marginUSD = marginUAH / usdRate;

        return {
            pid,
            name,
            sold: quantity,
            spend: Number(spendUSD.toFixed(2)),
            totalProfit: Number((marginUSD - spendUSD).toFixed(2)),
        };
    });



    const totalItems = result.reduce((sum, r) => sum + r.sold, 0);
    const totalOrders = ordersForReports.length;
    const avgItemsPerOrder = totalOrders > 0 ? (totalItems / totalOrders).toFixed(1) : "0";


    const withSalesAndSpend = result
        .filter(r => r.spend > 0 && r.sold > 0)
        .sort((a, b) => (a.costPerUnit || 0) - (b.costPerUnit || 0));

    const onlySales = result.filter(r => r.sold > 0 && r.spend === 0);

    const onlySpend = result.filter(r => r.spend > 0 && r.sold === 0);


    const handleCheckboxChange = () => {
        setIsChecked(prev => !prev);
    };


    const getOrdersByRange = (orders, range) => {
        const groups = {};

        orders.forEach(order => {
            const date = dayjs(order.created_at).tz(TIMEZONE);

            let key;

            switch (range) {
                case "day":
                    key = date.format("YYYY-MM-DD");
                    break;
                case "week":
                    key = date.startOf("isoWeek").format("YYYY-[W]WW");
                    break;
                case "2weeks":
                    const weekStart = date.startOf("isoWeek");
                    const twoWeekStart = weekStart.subtract(weekStart.isoWeek() % 2, 'week');
                    key = twoWeekStart.format("YYYY-[W]WW");
                    break;
                case "month":
                    key = date.format("YYYY-MM");
                    break;
                case "year":
                    key = date.format("YYYY");
                    break;
                default:
                    key = date.format("YYYY-MM-DD");
            }

            if (!groups[key]) groups[key] = [];
            groups[key].push(order);
        });

        return Object.values(groups);
    };


    const getGroupedMetricsByPeriod = (metricsType, orders, range, selectedSources) => {
        const groupedOrders = getOrdersByRange(orders, range);
        return groupedOrders.map(group => {
            const datesInGroup = [...new Set(group.map(o => dayjs(o.created_at).tz(TIMEZONE).format("YYYY-MM-DD")))];
            return datesInGroup.reduce((acc, date) => {
                const m = groupedByDate[date]?.[metricsType];
                if (!m) return acc;

                if (metricsType === "tickets") {
                    if (selectedSources.includes(1) || selectedSources.includes(4)) {
                        acc.instagram += Number(m.instagram || 0);
                    }
                    if (selectedSources.includes(2)) {
                        acc.telegram += Number(m.telegram || 0);
                    }
                }

                if (metricsType === "leads") {
                    if (selectedSources.includes(1) || selectedSources.includes(4)) {
                        acc.instagram += Number(m.instagram || 0); // ✅ доступ к полю
                    }
                    if (selectedSources.includes(2)) {
                        acc.telegram += Number(m.telegram || 0); // на будущее
                    }
                }

                return acc;
            }, { instagram: 0, telegram: 0 }); // ✅ начальные значения — ЧИСЛА
        });
    };



    const filteredTicketsByPeriod = getGroupedMetricsByPeriod(
        "tickets",
        ordersForReports,
        rangeDate,
        selectedSources
    );

    const filteredLeadsByPeriod = getGroupedMetricsByPeriod(
        "leads",
        ordersForReports,
        rangeDate,
        selectedSources
    );

    const getTotalLeads = () => {
        return leadsByPeriod.reduce((sum, day) => sum + getDayMetric(day || {}, selectedSources), 0);
    };

    const getTotalTickets = () => {
        return ticketsByPeriod.reduce((sum, day) => sum + getDayMetric(day || {}, selectedSources), 0);
    };
    const filteredLiveCampaigns = liveCampaigns.filter(campaign => {
        if (!selectedSources.length) return true;

        const isInstagram = ["lava1", "lava2", "lava4"].includes(campaign.source);
        const isSite = campaign.source === "lava3";

        // если выбран сайт (9)
        if (selectedSources.includes(9) && isSite) return true;

        // если выбраны инста источники (1 или 4)
        if ((selectedSources.includes(1) || selectedSources.includes(4)) && isInstagram) return true;

        return false;
    });



    const ordersByPeriodExtended = [...getOrdersByRange(ordersForReports, rangeDate)];
    const adsByPeriodExtended = [...adsByPeriod];
    if (liveOrders.length > 0 || liveCampaigns.length > 0) {
        adsByPeriodExtended.push(filteredLiveCampaigns);
    }

    const directManagerByDate = {};
    for (const s of summaries || []) {
        if (s?.date) directManagerByDate[s.date] = summaryDirectNames(s);
    }

    const periodDayKeys = ordersByPeriodExtended.map((periodOrders) =>
        periodOrders?.length ? dayjs(periodOrders[0].created_at).tz(TIMEZONE).format("YYYY-MM-DD") : ""
    );

    /** Актуальні менеджери Директу і київського шоуруму — з групп графіку змін. */
    const reportManagers = useMemo(() => {
        const firstYm = Object.keys(scheduleByYm || {}).sort()[0];
        return pickReportManagersFromSchedule(scheduleByYm?.[firstYm]?.participants);
    }, [scheduleByYm]);

    /** Хто з Директу на зміні по графіку (кілька — діалоги діляться порівну). */
    const directShiftUserIdsByPeriod = buildDirectShiftUserIdsByPeriod({
        periodDayKeys,
        scheduleByYm,
        directManagers: reportManagers.direct,
    });

    const directLabelByUserId = Object.fromEntries(
        reportManagers.direct.map((m) => [String(m.userId), m.label])
    );
    const directManagersByPeriod = periodDayKeys.map((dayKey, i) => {
        const onShift = directShiftUserIdsByPeriod[i] || [];
        if (onShift.length > 0) return onShift.map((id) => directLabelByUserId[id]).join(" + ");
        return dayKey ? (directManagerByDate[dayKey] || []).join(" + ") : "";
    });
    /** Лише Summary (для зіставлення з directLabel у fallback-режимі). */
    const summaryDirectManagersByPeriod = periodDayKeys.map((dayKey) =>
        dayKey ? directManagerByDate[dayKey] || [] : []
    );

    /** Конверсія діалогів → замовлення для обраних менеджерів Директу (Ліди / Summary). */
    const directManagerConversionRows = preset?.showDirectManagerDialogConversion
        ? computeDirectManagerDialogConversion({
              managers: reportManagers.direct,
              orders: ordersForReports,
              ordersByPeriodExtended,
              directManagersByPeriod: summaryDirectManagersByPeriod,
              directShiftUserIdsByPeriod,
              ticketsByPeriod: filteredTicketsByPeriod,
              selectedSources,
          })
        : null;

    const offlineManagerRows = preset?.showOfflineManagerStats
        ? computeOfflineManagerStats(ordersForReports, reportManagers.offline)
        : null;

    const managerScheduleMetaRows = useMemo(
        () => [
            ...(directManagerConversionRows || []).map((r) => ({
                reactKey: `direct-${r.key}`,
                userId: r.userId,
            })),
            ...(offlineManagerRows || []).map((r) => ({
                reactKey: `offline-${r.key}`,
                userId: r.userId,
            })),
        ],
        [directManagerConversionRows, offlineManagerRows]
    );

    const scheduleShiftStatsByReactKey = useMemo(
        () =>
            buildScheduleShiftStatsByReactKey(
                scheduleByYm,
                managerScheduleMetaRows,
                range.startDate,
                range.endDate
            ),
        [scheduleByYm, managerScheduleMetaRows, range.startDate, range.endDate]
    );
    const scheduleWorkingDaysByReactKey = scheduleShiftStatsByReactKey;

    useEffect(() => {
        let cancelled = false;
        if (!preset?.showDirectManagerDialogConversion && !preset?.showOfflineManagerStats) {
            setScheduleByYm({});
            setScheduleYmLoadStatus("idle");
            return;
        }
        if (!range?.startDate || !range?.endDate) {
            setScheduleByYm({});
            setScheduleYmLoadStatus("idle");
            return;
        }

        const yms = ymListIntersectingKyivRange(range.startDate, range.endDate);
        if (yms.length === 0) {
            setScheduleByYm({});
            setScheduleYmLoadStatus("ok");
            return;
        }

        (async () => {
            setScheduleYmLoadStatus("loading");
            try {
                const entries = await Promise.all(
                    yms.map(async (ym) => {
                        const res = await fetch(
                            `/api/schedule/month?ym=${encodeURIComponent(ym)}`,
                            { credentials: "include" }
                        );
                        if (!res.ok) {
                            const t = await res.text();
                            throw new Error(t || res.statusText);
                        }
                        const data = await res.json();
                        return [
                            ym,
                            {
                                shifts: data.shifts || {},
                                participants: data.participants || [],
                            },
                        ];
                    })
                );
                if (cancelled) return;
                setScheduleByYm(Object.fromEntries(entries));
                setScheduleYmLoadStatus("ok");
            } catch {
                if (cancelled) return;
                setScheduleByYm({});
                setScheduleYmLoadStatus("error");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        preset?.showDirectManagerDialogConversion,
        preset?.showOfflineManagerStats,
        range.startDate,
        range.endDate,
    ]);

    const instagramFollowersByDate = {};
    for (const s of summaries || []) {
        if (
            s?.date &&
            typeof s.instagramFollowers === "number" &&
            Number.isFinite(s.instagramFollowers)
        ) {
            instagramFollowersByDate[s.date] = s.instagramFollowers;
        }
    }

    const fmtIg = (x) => new Intl.NumberFormat("uk-UA").format(x);
    const formatInstagramFollowersCell = (cur, repYmd) => {
        if (cur == null || !Number.isFinite(cur)) return "—";
        const base = fmtIg(cur);
        // Попередній календарний день для ключа Summary (YYYY-MM-DD); без dayjs.tz() — у бандлі часто немає статичного dayjs.tz.
        const prevDay = dayjs(`${repYmd}T12:00:00`).subtract(1, "day").format("YYYY-MM-DD");
        const prev = instagramFollowersByDate[prevDay];
        if (prev == null || !Number.isFinite(prev)) return base;
        const delta = cur - prev;
        if (delta === 0) return `${base} (0)`;
        const sign = delta > 0 ? "+" : "";
        return `${base} (${sign}${fmtIg(delta)})`;
    };

    const hasLiveInstagramRow =
        preset?.fetchLiveToday &&
        filteredLiveOrders.length > 0 &&
        ordersByPeriodExtended.length > 0;

    const instagramFollowersDisplayByPeriod = ordersByPeriodExtended.map((periodOrders) => {
        const touchesKyivToday = Boolean(
            periodOrders?.some(
                (o) => dayjs(o.created_at).tz(TIMEZONE).format("YYYY-MM-DD") === kyivTodayYmd
            )
        );
        const isLiveTodayRow = hasLiveInstagramRow && touchesKyivToday;

        if (isLiveTodayRow) {
            if (
                liveInstagramFollowersCount != null &&
                Number.isFinite(liveInstagramFollowersCount)
            ) {
                return formatInstagramFollowersCell(liveInstagramFollowersCount, kyivTodayYmd);
            }
            if (liveInstagramFollowersLoading) return "Instagram…";
            return "—";
        }

        if (!periodOrders?.length) return "—";
        let rep = null;
        for (const o of periodOrders) {
            const d = dayjs(o.created_at).tz(TIMEZONE).format("YYYY-MM-DD");
            if (!rep || d > rep) rep = d;
        }
        if (!rep) return "—";
        const cur = instagramFollowersByDate[rep];
        if (cur == null) return "—";
        return formatInstagramFollowersCell(cur, rep);
    });

    const kyivWeatherByDateFromSummary = useMemo(() => {
        const m = {};
        for (const s of summaries || []) {
            if (s?.date && kyivWeatherDocHasForecastTemps(s.kyivWeather)) {
                m[s.date] = s.kyivWeather;
            }
        }
        return m;
    }, [summaries]);

    const weatherDisplayByPeriod = ordersByPeriodExtended.map((periodOrders) => {
        if (!periodOrders?.length) return null;
        /** Як колонка Instagram: брати «останній» YYYY-MM-DD у групі (не лише перше замовлення). */
        let dayKey = null;
        for (const o of periodOrders) {
            const ymd = dayjs(o.created_at).tz(TIMEZONE).format("YYYY-MM-DD");
            if (!dayKey || ymd > dayKey) dayKey = ymd;
        }
        if (!dayKey) return null;
        const fromSummary = kyivWeatherByDateFromSummary[dayKey];
        const fromOverlay = kyivWeatherOverlay[dayKey];
        const liveDailyRow = dayKey === kyivTodayYmd ? prLiveForecastDailyByDate[kyivTodayYmd] : null;

        if (kyivWeatherDocHasForecastTemps(fromSummary)) return fromSummary;
        if (kyivWeatherDocHasForecastTemps(liveDailyRow)) return liveDailyRow;
        if (kyivWeatherDocHasForecastTemps(fromOverlay)) return fromOverlay;

        if (
            shouldLogPrWeather() &&
            dayKey === kyivTodayYmd &&
            !kyivWeatherDocHasForecastTemps(fromSummary) &&
            !kyivWeatherDocHasForecastTemps(liveDailyRow) &&
            !kyivWeatherDocHasForecastTemps(fromOverlay)
        ) {
            console.debug("[PR weather] today row: no temps in summary / liveForecast / overlay", {
                dayKey,
                fromSummary,
                fromOverlay,
                liveDailyRow,
            });
        }
        return null;
    });

    console.log("adsByPeriodExtended", adsByPeriodExtended)

    const usdRateOk =
        usdSellRate != null && Number.isFinite(Number(usdSellRate)) && Number(usdSellRate) > 0;

    const formatCurrency = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return "—";
        if (currency === "USD") {
            if (!usdRateOk) return "—";
            return `$${(n / Number(usdSellRate)).toFixed(2)}`;
        }
        return `${n.toLocaleString()} грн.`;
    };
    const formatAdBudget = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return "—";
        if (currency === "USD") return `$${n.toFixed(2)}`;
        if (!usdRateOk) return "—";
        return `${(n * Number(usdSellRate)).toFixed(0).toLocaleString()} грн.`;
    };

    useEffect(() => {
        const groupedOrders = getOrdersByRange(ordersForReports, rangeDate);

        groupedOrders.forEach((orders, index) => {
            const firstOrderDate = orders.length > 0
                ? dayjs(orders[0].created_at).tz(TIMEZONE).format("YYYY-MM-DD")
                : `Группа ${index + 1}`;

            // console.log(`📅 Заказы за ${firstOrderDate}:`, orders);
        });

    }, [ordersForReports, rangeDate]);

    const botTOKEN = BOT_TOKEN
    const chat_id = CHAT_ID_AT

    const sendToTelegram = async () => {
        try {
            const escapeHtml = (text) =>
                text.replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;");

            const indent = " ".repeat(16);

            const sorted = [...withSalesAndSpend].sort((a, b) => {
                const roiA = parseFloat(a.roi);
                const roiB = parseFloat(b.roi);
                return roiB - roiA;
            });

            let totalSpend = 0;

            const formattedProducts = sorted.map(p => {
                const name = escapeHtml(p.name.slice(0, 25)).padEnd(25);
                const sold = String(p.sold).padStart(3);
                const spend = p.spend.toFixed(2).padStart(6);
                const cost = String(p.costPerUnit).padStart(5);

                totalSpend += p.spend;

                return `${name}\n${indent}${spend} / ${sold} = ${cost}`;
            });

            const formattedOnlySpend = onlySpend.map(p => {
                const name = escapeHtml(p.name.slice(0, 25)).padEnd(25);
                const spend = p.spend.toFixed(2).padStart(6);
                return `${name} = $${spend}`;
            });
            const sumSpendRed = onlySpend.reduce((sum, p) => sum + p.spend, 0).toFixed(2);

            const totalItems = withSalesAndSpend.reduce((sum, r) => sum + r.sold, 0);
            const avgItemsPerOrder = ordersForReports.length > 0 ? (totalItems / ordersForReports.length).toFixed(1) : "0";
            const avgSpendPerOrder = ordersForReports.length > 0 ? (totalSpend / ordersForReports.length).toFixed(2) : "0";

            const periodStart = formatRangeDayMonthInAppTz(range.startDate);
            const periodEnd = formatRangeDayMonthInAppTz(range.endDate);

            const baseMessage = `<b>Вигрузка ${periodStart} - ${periodEnd}</b>\n\n` +
                `Речей у одному продажу: ${avgItemsPerOrder}\n` +
                `Вартість продажу за період: $${avgSpendPerOrder}\n\n`;

            let currentMessage = baseMessage + `<pre>`;
            let parts = [];

            for (let line of formattedProducts) {
                if ((currentMessage + line + `</pre>\n\n<b>Сума:</b> ${totalSpend.toFixed(2)}`).length > 4096) {
                    currentMessage += `</pre>`;
                    parts.push(currentMessage);
                    currentMessage = `<pre>${line}\n`;
                } else {
                    currentMessage += line + "\n";
                }
            }

            currentMessage += `</pre>\n\n<b>Сума:</b> ${totalSpend.toFixed(2)}`;

            // добавляем блок с onlySpend, если есть
            if (formattedOnlySpend.length > 0) {
                currentMessage += `\n\n<b>Злитий бюджет без продажів:</b> (всього $${sumSpendRed})\n<pre>${formattedOnlySpend.join("\n")}</pre>`;
            }

            parts.push(currentMessage);

            for (const part of parts) {
                const payload = {
                    chat_id,
                    parse_mode: "html",
                    text: part
                };

                const res = await fetch(`https://api.telegram.org/${botTOKEN}/sendMessage`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });

                const data = await res.json();
                if (!res.ok || !data.result) {
                    console.error("❌ Помилка Telegram:", data);
                    throw new Error("Помилка під час відправлення частини повідомлення");
                }

                await fetch('/api/messages', {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json;charset=utf-8' },
                    body: JSON.stringify({
                        message_id: data.result.message_id,
                        message: part,
                        date: new Date().toISOString()
                    })
                });
            }

            console.log("✅ Сообщение отправлено");

        } catch (error) {
            console.error("❌ Помилка під час відправлення:", error);
        }
    };



    const handleUploadToSheet = async () => {
        try {
            const start = formatRangeDateForApi(range.startDate);
            const end = formatRangeDateForApi(range.endDate);
            const res = await fetch("/api/export/daily-product-matrix", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ start, end }),
            });

            const data = await res.json();

            if (!data.success) {
                throw new Error(data.message || "Export failed");
            }

            alert(`✅ Таблиця оновлена: ${data.result.sheetName}`);
        } catch (e) {
            console.error("❌ Upload error:", e);
            alert("Помилка при вигрузці в Google Sheets");
        }
    };

    const injectedCustomContent = typeof customContent === "function"
        ? customContent({ handleUploadToSheet })
        : customContent;

    return (
        <div className="d-flex layout-wrapper" style={{ height: '100%', minHeight: '100vh', transition: 'all 0.3s ease-in-out' }}>
        {showFilters && (
            <div ref={filtersPanelRef} style={{ width: 280, flexShrink: 0 }} className="bg-light border-end h-100 overflow-auto">
                    <HeaderControls
                        usdBuyRate={usdBuyRate}
                        usdSellRate={usdSellRate}
                        currency={currency}
                        setCurrency={setCurrency}
                        range={tempRange}
                        setRange={setTempRange}
                        onApplyRange={() => setRange(tempRange)}
                        onQuickRangeSelect={(r) => {
                            setTempRange(r);
                            setRange(r);
                        }}
                        isLoading={isLoading}
                        onRangeSelected={setRangeDate}
                        handleCheckboxChange={handleCheckboxChange}
                        isChecked={isChecked}
                        selectedManagers={selectedManagers}
                        setSelectedManagers={setSelectedManagers}
                        tagFilterMode={tagFilterMode}
                        setTagFilterMode={setTagFilterMode}
                        selectedSources={selectedSources}
                        toggleSource={(ids) =>
                            setSelectedSources(prev =>
                                prev.some(src => ids.includes(src))
                                    ? prev.filter(src => !ids.includes(src))
                                    : [...prev, ...ids]
                            )
                        }
                        toggleStatus={(id) =>
                            setSelectedStatuses(prev =>
                                prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
                            )
                        }
                        selectedStatuses={selectedStatuses}
                    />
                </div>
            )}

            <div className="main-content flex-grow-1 p-3">
                <h4 className="mb-3">{title}</h4>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <Button variant="dark" onClick={() => setShowFilters(prev => !prev)}>
                        <Sliders className="me-2" size={18} /> Налаштування
                    </Button>
                    {typeof primaryAction === "function" ? primaryAction({ handleUploadToSheet }) : primaryAction}
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowAdminButtons(prev => !prev)}
                        title={showAdminButtons ? "Сховати додаткові кнопки" : "Показати додаткові кнопки"}
                    >
                        {showAdminButtons ? <ChevronUp className="me-2" size={18} /> : <ChevronDown className="me-2" size={18} />}
                        {showAdminButtons ? "Сховати кнопки" : "Показати кнопки"}
                    </Button>
                    {topToolbarExtra}
                </div>
                {showAdminButtons && (
                    <>
                        {injectedCustomContent && <div className="mb-3">{injectedCustomContent}</div>}
                        {isAdmin && (
                            <div className="d-flex align-items-center mb-3 gap-2">
                                <AddToSummaryButton range={range} />
                                <DeleteFromSummaryButton range={range} />
                            </div>
                        )}
                    </>
                )}
                <MonthlyTurnoverProgressBar
                    model={monthTurnoverBarModel}
                    currency={currency}
                    usdSellRate={usdSellRate}
                />
                {monthTurnoverBarModel && monthTurnoverChannelRows.length > 0 ? (
                    <div className="row g-3 mb-3 align-items-start align-items-lg-stretch">
                        <div className="col-12 col-lg-4 d-flex">
                            <MonthTurnoverSourceSharePie channelRows={monthTurnoverChannelRows} />
                        </div>
                        <div className="col-12 col-lg-8 align-self-lg-stretch d-flex flex-column gap-2 mw-100 min-w-0">
                            {monthTurnoverChannelRows.map((row) => (
                                <div
                                    key={row.key}
                                    className="w-100 d-flex align-items-lg-stretch flex-lg-grow-1 align-self-stretch mw-100"
                                    style={{ minHeight: 0, flexBasis: "0%", minWidth: 0 }}
                                >
                                    <MonthlyTurnoverProgressBar
                                        model={row.model}
                                        currency={currency}
                                        usdSellRate={usdSellRate}
                                        heading={`${row.emoji} ${row.label}`}
                                        showMonthInHeading={false}
                                        compact
                                        expandVertically
                                        factShareOfMonthPct={row.factShareOfMonthPct}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {directManagerConversionRows || offlineManagerRows ? (
                    <DirectManagerDialogConversion
                        rows={directManagerConversionRows}
                        offlineRows={offlineManagerRows}
                        scheduleShiftStatsByReactKey={scheduleWorkingDaysByReactKey}
                        scheduleDaysLoadStatus={scheduleYmLoadStatus}
                        showScheduleWorkingDaysColumn={preset?.showManagerScheduleWorkingDays}
                    />
                ) : null}

                <OrdersTable
                    usdBuyRate={usdBuyRate}
                    usdSellRate={usdSellRate}
                    currency={currency}
                    visibleColumns={currentPreset?.columns || {}}
                    getTotalPurchased={getTotalPurchased}
                    getTotalMargin={getTotalMargin}
                    productBreakdownColumns={productBreakdownColumns}
                    combinedData={combinedData}
                    selectedSources={selectedSources}
                    // adsByPeriod={getAdsByRangeFromFiltered(filteredAds, ordersByPeriod, rangeDate)}
                    ordersByPeriod={ordersByPeriodExtended}
                    adsByPeriod={adsByPeriodExtended}
                    getTotalLeads={getTotalLeads}
                    formatCurrency={formatCurrency}
                    formatAdBudget={formatAdBudget}
                    getTotalPayments={getTotalPayments}
                    getTotalUpsales={getTotalUpsales}
                    getRepeatSalesWithTag5={getRepeatSalesWithTag5}
                    getSaleOrdersWithTag6={getSaleOrdersWithTag6}
                    summaries={summaries}
                    leadsByPeriod={filteredLeadsByPeriod}
                    rangeDate={rangeDate}
                    ticketsByPeriod={filteredTicketsByPeriod}
                    getTotalTickets={getTotalTickets}
                    directManagersByPeriod={directManagersByPeriod}
                    instagramFollowersDisplayByPeriod={instagramFollowersDisplayByPeriod}
                    weatherDisplayByPeriod={weatherDisplayByPeriod}
                />
                {showProductTables && (
                    <div className="mt-4">
                        <h5>1️⃣ Окупність</h5>
                        <table className="table table-upload table-sm table-bordered">
                            <thead>
                            <tr>
                                <th>Товар</th>
                                <th>ROI</th>
                                <th>Продано/шт</th>
                                <th>Бюджет $</th>
                                <th>Ціна продажу $</th>
                                {isAdmin && <th>Маржа/шт</th>}
                                {isAdmin && <th>Профіт/шт</th>}
                                {isAdmin && <th>Загальний профіт</th>}
                            </tr>
                            </thead>
                            <tbody>
                            {[...withSalesAndSpend].sort((a, b) => {
                                const roiA = parseFloat(a.roi); // "7659.3%" -> 7659.3
                                const roiB = parseFloat(b.roi);
                                return roiB - roiA;
                            }).map(row => (
                                <tr key={row.name}>
                                    <td className="text-start">{row.name}</td>
                                    <td>{row.roi}</td>
                                    <td>{row.sold}</td>
                                    <td>{row.spend}</td>
                                    <td>{row.costPerUnit}</td>
                                    {isAdmin && <td>{row.unitMargin}</td>}
                                    {isAdmin && <td>{row.unitProfit}</td>}
                                    {isAdmin && <td>{row.totalProfit}</td>}
                                </tr>
                            ))}
                            </tbody>
                            <tfoot>
                            <tr className="fw-bold">
                                <td className="text-start">Всього:</td>
                                <td></td>
                                <td>{withSalesAndSpend.reduce((sum, r) => sum + r.sold, 0)}</td>
                                <td>{withSalesAndSpend.reduce((sum, r) => sum + r.spend, 0).toFixed(2)}</td>
                                {isAdmin && <td colSpan={2}></td>}
                                <td></td>
                                {isAdmin && <td>{withSalesAndSpend.reduce((sum, r) => sum + r.totalProfit, 0).toFixed(2)}</td>}
                            </tr>
                            </tfoot>
                        </table>
                        <h5 className="mt-4">
                            2️⃣ Вигрузка ({formatRangeDayMonthInAppTz(range.startDate)} – {formatRangeDayMonthInAppTz(range.endDate)})
                            <button
                                type="submit"
                                style={{
                                    marginTop: '30px',
                                    marginBottom: '10px',
                                    backgroundColor: '#3f51b5',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '5px',
                                    padding: '10px 20px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                }}
                                onClick={sendToTelegram}
                            >
                                Надіслати
                            </button></h5>
                        <div className="d-flex mt-4 gap-4">
                            <div style={{ flex: 1 }}>
                                <h6>
                                    Продажі з рекламою [ бюджет ${withSalesAndSpend.reduce((sum, r) => sum + r.spend, 0).toFixed(2)} ]
                                </h6>
                                <p className="mb-1">
                                    Речей у одному продажу: {avgItemsPerOrder}
                                </p>
                                <p className="mb-3">
                                    Вартість продажу за період:{" $"}
                                    {(result.reduce((sum, r) => sum + r.spend, 0)/totalOrders).toFixed(2)}
                                </p>
                                <ul className="list-group">
                                    {withSalesAndSpend.map(r => (
                                        <li className="list-group-item border-0 mb-0" key={r.name}>
                                            {r.name}: {r.spend}/{r.sold} = {r.costPerUnit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h6>Продажі без реклами [ кількість {onlySales.reduce((sum, r) => sum + r.sold, 0)} шт. ]</h6>
                                <ul className="list-group">
                                    {onlySales.map(r => (
                                        <li className="list-group-item border-0 mb-0" key={r.name}>
                                            {r.name}: {r.sold} шт.
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h6>Злитий бюджет без продажів [ бюджет ${onlySpend.reduce((sum, r) => sum + r.spend, 0).toFixed(2)} ] <i className="bi bi-info-circle"
                                                                  title="Бюджет на рекламу, яка не дала жодного замовлення за вибраний період."></i>
                                </h6>
                                <ul className="list-group">
                                    {onlySpend.map(r => (
                                        <li className="list-group-item border-0 mb-0" key={r.name}>
                                            {r.name}: ${r.spend}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportLayout;
